import { useState, useRef, useEffect, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import type { User } from '../../lib/gotrueTypes'

/** Only the fields this menu reads, projected from the real auth user type
 * (`useAuth()` returns `User | null`) so this component can't drift from it. */
type UserMenuUser = Pick<User, 'email' | 'user_metadata'>

/**
 * Shared user menu (avatar trigger + dropdown chrome) — single source of truth for
 * the profile menu across the landing (NavbarV2) and the app (layout/Header).
 * Canonical look = the landing: a teal round avatar and a dark dropdown. Menu items
 * are composed per-surface via the `children` render prop so each surface keeps its
 * own actions (the app additionally exposes Cookie settings) while the chrome stays
 * identical. See Workplace post 1562612608829370.
 */
export default function UserMenu({
  user,
  testIdPrefix,
  accountTitle,
  avatarAlt,
  userFallbackLabel,
  leading,
  children,
}: {
  user: UserMenuUser
  /** e.g. 'header' (app) or 'homenav' (landing) — keeps each surface's test ids. */
  testIdPrefix: string
  accountTitle: string
  avatarAlt: string
  userFallbackLabel: string
  /** Optional content inside the trigger pill, left of the avatar (e.g. the
   * active team's name). Renders nothing extra when omitted. */
  leading?: ReactNode
  /** Menu item buttons; `close` dismisses the dropdown. */
  children: (close: () => void) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target
      if (target instanceof Node && menuRef.current && !menuRef.current.contains(target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const close = () => setOpen(false)
  const avatarUrl = user.user_metadata?.avatar_url
  const initial = user.email?.charAt(0).toUpperCase() || 'U'

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testIdPrefix}-user-menu-button`}
        // `aria-haspopup`/`aria-expanded` accompany the chevron: a disclosure
        // caret that only exists visually tells assistive tech nothing.
        aria-haspopup="menu"
        aria-expanded={open}
        // Avatar + chevron live inside their own bordered pill, as drawn in
        // Rowell's mockup (~81x42 at a 1440 viewport) — the caret reads as part
        // of one account control rather than a loose glyph beside the avatar.
        // Height comes from the avatar (36) + p-1 (8) = 44, so the pill tracks
        // the avatar if that ever changes.
        className="flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 p-1 pr-2.5 hover:border-white/25 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 transition-all cursor-pointer"
        title={user.email || accountTitle}
      >
        {leading}
        {avatarUrl ? (
          <img src={avatarUrl} alt={avatarAlt} className="w-9 h-9 rounded-full" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#8dd5d8] to-[#5a9ba0] flex items-center justify-center text-black text-sm font-semibold">
            {initial}
          </div>
        )}
        <ChevronDown
          aria-hidden="true"
          strokeWidth={2}
          className={`h-4 w-4 shrink-0 text-white/60 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-48 bg-[#1a1a1a] border border-white/10 rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.8)] py-1 z-50"
          data-testid={`${testIdPrefix}-user-menu`}
        >
          <div className="px-3 py-2.5 border-b border-white/10">
            <p className="text-sm font-medium truncate text-white">
              {user.user_metadata?.full_name || userFallbackLabel}
            </p>
            <p className="text-xs font-medium text-white/50 truncate mt-0.5">{user.email}</p>
          </div>
          {children(close)}
        </div>
      )}
    </div>
  )
}

/** Shared menu item — consistent styling for every entry in the dropdown. */
export function UserMenuItem({
  onClick,
  icon,
  children,
  testId,
  trailing,
}: {
  onClick: () => void
  icon: ReactNode
  children: ReactNode
  testId?: string
  /** Right-aligned slot, e.g. the credit balance on the usage row. */
  trailing?: ReactNode
}) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="w-full px-3 py-2 text-left text-sm text-white/60 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/30 focus-visible:text-white transition-colors flex items-center gap-2 cursor-pointer"
    >
      {icon}
      {children}
      {trailing !== undefined && <span className="ml-auto pl-3 shrink-0">{trailing}</span>}
    </button>
  )
}
